import React, { useCallback, useMemo, useRef } from "react";
import classnames from "classnames";
import ClickOutside from "react-click-outside";

export interface DropdownProps {
	id: string;
	align?: string;
	children: React.ReactNode;
	className?: string;
	disabled?: boolean;
	menuBtn: React.ReactElement;
	close: () => void;
	open: () => void;
	toggle: () => void;
	active: number | null;
	isOpen: boolean;
}
export interface MenuBtnProps {
	id: string;
	className?: string;
	disabled?: boolean;
	"aria-haspopup": boolean;
	"aria-expanded": boolean;
	onClick: () => void;
	ref: React.RefObject<HTMLButtonElement>;
}

function ActivityInstancesDropdown({
	id,
	align = "left",
	children,
	className,
	disabled,
	menuBtn,
	close,
	open,
	toggle,
	active,
	isOpen,
}: DropdownProps) {
	const menuBtnRef: { current: null | HTMLButtonElement } = React.createRef();
	const childRefs = useRef<Array<React.RefObject<HTMLElement>>>([]);

	// Build our dropdown children and memoize them so that they aren't
	// rebuilt on every render
	const dropdownChildren = useMemo(() => {
		childRefs.current = [];
		let i = 0;
		return React.Children.map(children, child => {
			if (
				!React.isValidElement(child) ||
				child.props["data-dropdownignore"] === true
			) {
				return child;
			} else {
				const props: { ref?: React.RefObject<HTMLElement> } = {};
				childRefs.current[i] = React.createRef<HTMLElement>();
				props.ref = childRefs.current[i];
				i++;
				return React.cloneElement(child, props);
			}
		});
	}, [children]);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (
				event.key === "Escape" ||
				event.key === "ArrowDown" ||
				event.key === "ArrowUp"
			) {
				event.preventDefault();
				event.stopPropagation();
			}
			if (!event.shiftKey && event.key === "Tab" && active === 0) {
				close();
			}
		},
		[close, active]
	);

	const handleKeyUp = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			// opens the droplist and returns if it's currently closed
			if (!isOpen) {
				switch (event.key) {
					case "ArrowDown":
					case "ArrowUp":
						open();
						break;
					default:
						break;
				}
			} else {
				// runs switch if the droplist is already opened
				switch (event.key) {
					case "Escape":
						close();
						if (menuBtnRef.current) menuBtnRef.current.focus();
						break;
					default:
						break;
				}
			}
		},
		[close, isOpen, menuBtnRef, open]
	);

	// Wrapper classes
	const wrapperClasses = useMemo(() => {
		return classnames([
			className,
			isOpen && "show",
			align === "top-right" || align === "top-left"
				? "dropup"
				: "dropdown",
		]);
	}, [align, className, isOpen]);

	// Option classes
	const optionClasses = useMemo(() => {
		return classnames([
			"dropdown-menu",
			"shadow",
			align === "top-right" || align === "right"
				? "dropdown-menu-end"
				: null,
		]);
	}, [align]);

	const menuBtnWithProps = useMemo(() => {
		const menuBtnProps: MenuBtnProps = {
			id,
			"aria-haspopup": true,
			"aria-expanded": isOpen,
			className: classnames([
				menuBtn.props.className,
				isOpen && "form-select--open",
			]),
			disabled,
			onClick: toggle,
			ref: menuBtnRef,
		};
		return React.cloneElement(menuBtn, menuBtnProps);
	}, [menuBtn, isOpen, disabled, toggle, menuBtnRef, id]);

	return (
		<div
			className={wrapperClasses}
			onKeyUp={handleKeyUp}
			onKeyDown={handleKeyDown}>
			{menuBtnWithProps}
			{isOpen ? (
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				<ClickOutside<any> onClickOutside={toggle}>
					<div
						data-bs-popper
						className={optionClasses}
						aria-labelledby={id}>
						{dropdownChildren}
					</div>
				</ClickOutside>
			) : null}
		</div>
	);
}

export default React.memo(ActivityInstancesDropdown);
